/**
 * Monetization API — the two reads the popup needs, plus the cancel path.
 *
 * Same `apiCall` shape as `mailboxApi.ts` (auth headers from AuthUtils, VITE_API_URL base), and it
 * calls ONLY routes that already exist on the backend (P091 E4/E5):
 *   POST /api/v1/subscription/my_subscription    -> credit balance + the tier + that tier's rate
 *   POST /api/v1/subscription/portal             -> Stripe Customer Portal URL (D-059: cancel is Stripe's)
 *
 * (`POST /api/v1/credits/get_credit_balance` exists and works, but returns the balance with no tier,
 * so nothing in the FE reads it — see `getMySubscription`.)
 *
 * There is deliberately no "create subscription" call here: the purchase path is the Stripe
 * Payment Link (a hosted page), which is what makes this a one-evening port rather than a rebuild.
 */
import AuthUtils from "@/providers/firebase/authUtils";
import { ApiResponse } from "./kernelBotApi";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

async function apiCall<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
  const headers = await AuthUtils.getAuthHeaders();
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  return res.json();
}

export interface MySubscription {
  /**
   * THE KEY IS `credit_balance`. Verified live against DEV on 2026-09-21 — the payload is
   * `{entity_id, entitykey, credit_balance, credit_grants_received, tier, per_send_credit_cost}`.
   *
   * The interface used to declare `credits?` and `balance?` and the caller read `credits ?? balance`,
   * so BOTH names were wrong and a balance that had genuinely been fetched was silently dropped: the
   * wallet showed "reading your credit balance" for ever. The names came from glowtest's backend,
   * which returns `current_credits` — the port copied the call and not this repo's key. Declaring the
   * real shape here is what makes that un-repeatable: a wrong key is now a TYPE error, not a silent
   * `undefined`.
   */
  credit_balance?: number;
  /** Lifetime grants, kept for the "how much did I ever get" reading. */
  credit_grants_received?: number;
  entity_id?: string | null;
  entitykey?: string | null;
  /** `"free"` for an account with no grant; the plan key (`starter`/`growth`/`scale`) otherwise. */
  tier?: string | null;
  /** What ONE reachout costs this payer, from the same kernel the blog and the plan cards quote. */
  per_send_credit_cost?: number;
  status?: string | null;
  current_period_end?: string | null;
  cancel_at_period_end?: boolean;
  stripe_customer_id?: string | null;
  /**
   * The First-Conversation Guarantee, as THIS account's plan states it (P091 D-132).
   *
   * `null` when the plan does not sell it — Free and Starter — which is why a surface must render
   * NOTHING for `null` rather than defaulting to a friendly line: the absence means "this promise was
   * never made to you", not "we could not read it".
   */
  guarantee?: GuaranteeStanding | null;
}

/** What the buyer may be told about the promise — the state, the term, and the counts she can check. */
export interface GuaranteeStanding {
  /**
   * Closed vocabulary: `kept` | `in_progress` | `owed`.
   *
   * Typed as `string` rather than a union on purpose, so an unrecognised value arrives as itself
   * instead of being narrowed into a lie — the renderer then declines to phrase it (a state we cannot
   * word is one we must not guess at).
   */
  state: string;
  term_reachouts: number;
  reachouts_sent: number;
  reachouts_left_in_term: number;
  /** The POSITION of the reply inside the window (1-based), or null when no one answered. */
  first_reply_at_reachout: number | null;
}

/**
 * The ONE read the monetisation surfaces make.
 *
 * `/subscription/my_subscription` is chosen over `/credits/get_credit_balance` deliberately: it
 * returns the balance object PLUS `tier` PLUS `per_send_credit_cost`, where the credits route returns
 * the balance alone. The wallet needs the balance and the dialog needs the plan, so using the
 * balance-only route meant fetching two facts with two calls and forgetting the second — which is
 * exactly what happened (the tier read was pointed at a payload that never contained one).
 */
export async function getMySubscription(): Promise<ApiResponse<MySubscription>> {
  return apiCall<MySubscription>("/api/v1/subscription/my_subscription");
}

/** Stripe Customer Portal (D-059) — cancelling is Stripe's page, not a bespoke in-app flow. */
export async function getSubscriptionPortalUrl(): Promise<ApiResponse<{ url?: string }>> {
  return apiCall<{ url?: string }>("/api/v1/subscription/portal");
}

/** What a top-up session comes back with: the Stripe URL to open, and the credits that amount buys. */
export interface CreditsCheckout {
  url?: string;
  amount_cents?: number;
  /** Computed by the backend from the kernel's peg — the same number the grant will use. */
  credits?: number;
  success_url?: string;
}

/**
 * OPEN A TOP-UP FOR THE AMOUNT CHOSEN ON OUR CARD (P091, PO 2026-09-21).
 *
 * Why this exists rather than just opening the Add-credits Payment Link: a Payment Link is
 * pay-what-you-want — the amount is typed at STRIPE, so our card could only describe it. There is no
 * parameter that tells a Payment Link an amount, so the choice moving onto our card means the session is
 * created by our backend, which sets the two things the grant needs (`client_reference_id` = the uid,
 * `metadata.tier` = "credits") and returns the URL to open.
 *
 * The caller must NOT fall back to opening some other payment page when this fails: the amount is the
 * customer's choice, and a page that asks him for it again is a different decision, not a recovery.
 */
export async function startCreditsCheckout(amountUsd: number): Promise<ApiResponse<CreditsCheckout>> {
  return apiCall<CreditsCheckout>("/api/v1/subscription/checkout-credits", { amount_usd: amountUsd });
}
